import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MailOpen, Send, Users, Loader2, RefreshCw, UserX, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SmartReEngagement({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTo, setSendingTo] = useState(null);
  const [customMessage, setCustomMessage] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSuggestions = () => {
    setLoading(true);
    axios.get(`${API}/api/admin/re-engagement/suggestions`, { headers })
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load suggestions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (token) fetchSuggestions(); }, [token]);

  const sendEmail = async (suggestion) => {
    setSendingTo(suggestion.email);
    try {
      await axios.post(`${API}/api/admin/re-engagement/send`, {
        email: suggestion.email,
        name: suggestion.name,
        message: customMessage || undefined
      }, { headers });
      toast.success(`Re-engagement email sent to ${suggestion.email}`);
    } catch { toast.error('Failed to send'); }
    finally { setSendingTo(null); }
  };

  return (
    <Card data-testid="smart-reengagement">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MailOpen className="w-5 h-5 text-purple-600" /> Smart Re-Engagement
            </CardTitle>
            <CardDescription>Customers who opened emails but never ordered</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchSuggestions} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-300" /></div>
        ) : !data || data.suggestions?.length === 0 ? (
          <div className="py-8 text-center text-neutral-400">
            <UserX className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No re-engagement opportunities found</p>
            <p className="text-xs mt-1">Once campaigns are sent and tracked, suggestions appear here</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-4 text-xs">
              <span className="flex items-center gap-1 text-neutral-500"><Users className="w-3.5 h-3.5" /> {data.total_openers} total openers</span>
              <span className="flex items-center gap-1 text-purple-600"><UserX className="w-3.5 h-3.5" /> {data.total_non_converters} haven't ordered</span>
            </div>

            {/* Custom message */}
            <div className="mb-4">
              <Input
                value={customMessage}
                onChange={e => setCustomMessage(e.target.value)}
                placeholder="Optional: customize your re-engagement message..."
                className="text-sm"
                data-testid="reengagement-message"
              />
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="reengagement-list">
              {data.suggestions.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border hover:bg-neutral-100 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-neutral-800 truncate">{s.name || s.email}</p>
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">{s.open_count} opens</span>
                    </div>
                    <p className="text-xs text-neutral-500">{s.email}</p>
                    {s.last_quote_date && (
                      <p className="text-[10px] text-neutral-400 mt-0.5">Last quote: {new Date(s.last_quote_date).toLocaleDateString()}</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendEmail(s)}
                    disabled={sendingTo === s.email}
                    className="shrink-0 ml-3"
                    data-testid={`send-reengagement-${i}`}
                  >
                    {sendingTo === s.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Send
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
